import { ContentPageHeader } from '@/components/global';
import { DeleteStockTakeButton } from '@/components/inventory';
import { ProductIdentity, ProductSelect, type ProductSelectOption } from '@/components/products';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiFetch } from '@/lib/api';
import { formatApiErrorBody } from '@/lib/formatApiError';
import { formatCaseUnits } from '@/lib/formatCaseUnits';
import { toProductSelectOption } from '@/lib/productCatalog';
import { cn } from '@/lib/utils';
import { Check, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface StockTakeLine {
  id: string;
  productId: string;
  systemUnits: number;
  casesCounted: number;
  leftoverUnits: number;
  batchNumber?: string | null;
  expiryDate?: string | null;
  isDeferred?: boolean;
  product?: {
    name: string;
    sku?: string;
    unitsPerCase?: number;
    barcode?: string | null;
    unitBarcode?: string | null;
    caseBarcode?: string | null;
    thumbnailUrl?: string | null;
    imageUrl?: string | null;
    media?: Array<{ url: string; mediaType: string }> | null;
  };
}

interface StockTakeDetail {
  id: string;
  status: string;
  isInitial?: boolean;
  warehouseId: string;
  warehouse?: { id: string; name: string };
  lines?: StockTakeLine[];
}

interface LineDraft {
  cases: string;
  leftover: string;
  batch: string;
  expiry: string;
}

function draftFromLine(line: StockTakeLine): LineDraft {
  return {
    cases: String(line.casesCounted),
    leftover: String(line.leftoverUnits),
    batch: line.batchNumber ?? '',
    expiry: line.expiryDate ?? '',
  };
}

function parseDraft(draft: LineDraft, unitsPerCase: number): { cases: number; leftover: number } {
  const upc = Math.max(1, unitsPerCase);
  const singleUnitPack = upc === 1;
  const cases = Math.max(0, parseInt(draft.cases, 10) || 0);
  const leftover = singleUnitPack ? 0 : Math.max(0, parseInt(draft.leftover, 10) || 0);
  return { cases, leftover };
}

function draftMatchesLine(draft: LineDraft, line: StockTakeLine): boolean {
  const upc = line.product?.unitsPerCase ?? 1;
  const parsed = parseDraft(draft, upc);
  return (
    parsed.cases === line.casesCounted &&
    parsed.leftover === line.leftoverUnits &&
    draft.batch.trim() === (line.batchNumber ?? '') &&
    draft.expiry === (line.expiryDate ?? '')
  );
}

function draftCountedUnits(draft: LineDraft, line: StockTakeLine): number {
  const upc = Math.max(1, line.product?.unitsPerCase ?? 1);
  const { cases, leftover } = parseDraft(draft, upc);
  return cases * upc + leftover;
}

/** Initial stock takes require batch + expiry before a counted line can be saved. */
function draftNeedsBatchDetails(isInitial: boolean, draft: LineDraft, line: StockTakeLine): boolean {
  if (!isInitial || line.isDeferred) return false;
  if (draftCountedUnits(draft, line) === 0) return false;
  return !draft.batch.trim() || !draft.expiry;
}

function isDeferredLinePayload(value: unknown): value is {
  casesCounted: number;
  leftoverUnits: number;
  batchNumber?: string | null;
  expiryDate?: string | null;
  isDeferred: boolean;
} {
  if (typeof value !== 'object' || value === null) return false;
  const row = value as Record<string, unknown>;
  return (
    typeof row.casesCounted === 'number' &&
    typeof row.leftoverUnits === 'number' &&
    typeof row.isDeferred === 'boolean'
  );
}

export default function StockTakeCountPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [st, setSt] = useState<StockTakeDetail | null>(null);
  const [drafts, setDrafts] = useState<Record<string, LineDraft>>({});
  const [productFilterId, setProductFilterId] = useState('');
  const [savingLineId, setSavingLineId] = useState<string | null>(null);
  const [deferringLineId, setDeferringLineId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assortmentOptions, setAssortmentOptions] = useState<ProductSelectOption[]>([]);
  const [assortmentError, setAssortmentError] = useState<string | null>(null);
  const [resuming, setResuming] = useState(false);

  const draftsRef = useRef(drafts);
  const stRef = useRef(st);
  draftsRef.current = drafts;
  stRef.current = st;

  const load = useCallback(async () => {
    if (!id) return;
    const res = await apiFetch(`/api/v1/stock-takes/${id}`);
    if (res.ok) {
      const data: StockTakeDetail = await res.json();
      setSt(data);
      const nextDrafts: Record<string, LineDraft> = {};
      for (const line of data.lines ?? []) {
        nextDrafts[line.productId] = draftFromLine(line);
      }
      setDrafts(nextDrafts);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!st?.warehouseId) {
      setAssortmentOptions([]);
      return;
    }
    let cancelled = false;
    setAssortmentError(null);
    apiFetch(`/api/v1/warehouses/${st.warehouseId}/products?pageSize=500`)
      .then(async (res) => {
        if (!res.ok) throw new Error(formatApiErrorBody(await res.json().catch(() => ({}))));
        const data = await res.json();
        if (cancelled) return;
        const options = (data.items ?? [])
          .map((item: { product?: unknown }) =>
            item.product ? toProductSelectOption(item.product) : null
          )
          .filter((option: ProductSelectOption | null): option is ProductSelectOption => option != null);
        setAssortmentOptions(options);
      })
      .catch((e) => {
        if (!cancelled) {
          setAssortmentError(e instanceof Error ? e.message : 'Failed to load location assortment');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [st?.warehouseId]);

  const canEdit = st?.status === 'COUNTING';
  const isInitial = st?.isInitial === true;
  const allLines = useMemo(() => st?.lines ?? [], [st?.lines]);
  const countableLines = useMemo(
    () => allLines.filter((line) => line.product?.name),
    [allLines]
  );

  const lineOptions = useMemo((): ProductSelectOption[] => {
    return countableLines
      .map((line) => {
        const product = line.product;
        if (!product?.name) return null;
        return toProductSelectOption({
          id: line.productId,
          sku: product.sku ?? line.productId,
          name: product.name,
          barcode: product.barcode,
          unitBarcode: product.unitBarcode,
          caseBarcode: product.caseBarcode,
          unitsPerCase: product.unitsPerCase,
          thumbnailUrl: product.thumbnailUrl,
          imageUrl: product.imageUrl,
          media: product.media,
        });
      })
      .filter((option): option is ProductSelectOption => option !== null);
  }, [countableLines]);

  const productOptions = assortmentOptions.length > 0 ? assortmentOptions : lineOptions;

  const handleProductSelect = useCallback(
    async (productId: string) => {
      setProductFilterId(productId);
      if (!productId || !canEdit) return;
      if (allLines.some((line) => line.productId === productId)) return;
      await load();
    },
    [allLines, canEdit, load]
  );

  const filteredProduct = productFilterId
    ? productOptions.find((product) => product.id === productFilterId)
    : undefined;

  const persistLine = useCallback(
    async (line: StockTakeLine, draft: LineDraft): Promise<boolean> => {
      if (!id || !canEdit) return false;

      const upc = line.product?.unitsPerCase ?? 1;
      const { cases, leftover } = parseDraft(draft, upc);

      if (!draftMatchesLine(draft, line)) {
        if (!singleUnitPackValid(upc, leftover)) {
          setError(`Leftover units must be 0–${Math.max(0, upc - 1)} for ${line.product?.name ?? 'product'}.`);
          return false;
        }
      } else {
        return true;
      }

      const batch = draft.batch.trim();
      const expiry = draft.expiry;

      setSavingLineId(line.id);
      setError(null);
      try {
        const res = await apiFetch(`/api/v1/stock-takes/${id}/lines`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            productId: line.productId,
            casesCounted: cases,
            leftoverUnits: leftover,
            ...(batch ? { batchNumber: batch } : {}),
            ...(expiry ? { expiryDate: expiry } : {}),
          }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(formatApiErrorBody(data));
        }

        const saved = await res.json() as { removed?: boolean };
        if (saved.removed === true) {
          await load();
          return true;
        }

        setSt((prev) => {
          if (!prev?.lines) return prev;
          return {
            ...prev,
            lines: prev.lines.map((l) =>
              l.id === line.id
                ? {
                    ...l,
                    casesCounted: cases,
                    leftoverUnits: leftover,
                    batchNumber: batch || null,
                    expiryDate: expiry || null,
                  }
                : l
            ),
          };
        });
        setDrafts((prev) => ({
          ...prev,
          [line.productId]: {
            cases: String(cases),
            leftover: String(leftover),
            batch,
            expiry,
          },
        }));
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to save count');
        return false;
      } finally {
        setSavingLineId(null);
      }
    },
    [canEdit, id, load]
  );

  const saveLineIfChanged = useCallback(
    async (line: StockTakeLine, options?: { strict?: boolean }): Promise<boolean> => {
      if (line.isDeferred) return true;
      const draft = drafts[line.productId] ?? draftFromLine(line);
      if (draftMatchesLine(draft, line)) return true;
      if (options?.strict && draftNeedsBatchDetails(isInitial, draft, line)) {
        setError(`Enter batch number and expiry date for ${line.product?.name ?? 'product'}.`);
        return false;
      }
      return persistLine(line, draft);
    },
    [drafts, isInitial, persistLine]
  );

  const flushPendingLines = useCallback(async (): Promise<number> => {
    const currentSt = stRef.current;
    if (!id || !currentSt || currentSt.status !== 'COUNTING') return 0;

    const currentDrafts = draftsRef.current;
    let remaining = 0;
    for (const line of currentSt.lines ?? []) {
      if (line.isDeferred) continue;
      const draft = currentDrafts[line.productId] ?? draftFromLine(line);
      if (draftMatchesLine(draft, line)) continue;
      const ok = await persistLine(line, draft);
      if (!ok) remaining += 1;
    }
    return remaining;
  }, [id, persistLine]);

  const unsavedCount = useMemo(
    () => countableLines.filter((line) => !line.isDeferred && !draftMatchesLine(drafts[line.productId] ?? draftFromLine(line), line)).length,
    [countableLines, drafts]
  );

  useEffect(() => {
    if (!canEdit) return;

    const flushOnHide = () => {
      if (document.visibilityState === 'hidden') {
        void flushPendingLines();
      }
    };

    window.addEventListener('pagehide', flushOnHide);
    document.addEventListener('visibilitychange', flushOnHide);
    return () => {
      window.removeEventListener('pagehide', flushOnHide);
      document.removeEventListener('visibilitychange', flushOnHide);
    };
  }, [canEdit, flushPendingLines]);

  useEffect(() => {
    return () => {
      void flushPendingLines();
    };
  }, [flushPendingLines]);

  useEffect(() => {
    if (!canEdit || unsavedCount === 0) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [canEdit, unsavedCount]);

  async function leaveCountPage() {
    const remaining = await flushPendingLines();
    if (remaining > 0) {
      setError('Some counts could not be saved. Fix validation errors before leaving.');
      return;
    }
    navigate('/inventory/stock-takes');
  }

  async function resumeCounting() {
    if (!id) return;
    setResuming(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/v1/stock-takes/${id}/return-to-counting`, { method: 'PATCH' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(formatApiErrorBody(data));
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not resume counting');
    } finally {
      setResuming(false);
    }
  }

  async function submitReview() {
    if (!id) return;
    setSubmitting(true);
    setError(null);
    try {
      for (const line of countableLines) {
        const ok = await saveLineIfChanged(line, { strict: true });
        if (!ok) return;
      }

      const res = await apiFetch(`/api/v1/stock-takes/${id}/submit-review`, { method: 'PATCH' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(formatApiErrorBody(data));
      }
      navigate(`/inventory/stock-takes/${id}/review`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to submit for review');
    } finally {
      setSubmitting(false);
    }
  }

  const lines = countableLines.filter((line) => {
    if (!productFilterId) return true;
    return line.productId === productFilterId;
  });

  const counted = countableLines.filter((l) => !l.isDeferred && (l.casesCounted > 0 || l.leftoverUnits > 0)).length;
  const deferredCount = countableLines.filter((l) => l.isDeferred).length;

  async function setLineDeferred(line: StockTakeLine, deferred: boolean) {
    if (!id || !canEdit) return;
    setDeferringLineId(line.id);
    setError(null);
    try {
      const res = await apiFetch(`/api/v1/stock-takes/${id}/lines/${line.id}/defer`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deferred }),
      });
      const data: unknown = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(formatApiErrorBody(data));
      }
      if (!isDeferredLinePayload(data)) {
        throw new Error('Stock take line response was invalid');
      }
      const nextLine: StockTakeLine = {
        ...line,
        casesCounted: data.casesCounted,
        leftoverUnits: data.leftoverUnits,
        batchNumber: typeof data.batchNumber === 'string' ? data.batchNumber : null,
        expiryDate: typeof data.expiryDate === 'string' ? data.expiryDate : null,
        isDeferred: data.isDeferred,
      };
      setSt((prev) => {
        if (!prev?.lines) return prev;
        return {
          ...prev,
          lines: prev.lines.map((item) => (item.id === line.id ? nextLine : item)),
        };
      });
      setDrafts((prev) => ({
        ...prev,
        [line.productId]: draftFromLine(nextLine),
      }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to defer product');
    } finally {
      setDeferringLineId(null);
    }
  }

  function updateDraft(productId: string, field: keyof LineDraft, value: string) {
    setDrafts((prev) => ({
      ...prev,
      [productId]: {
        ...(prev[productId] ?? { cases: '', leftover: '', batch: '', expiry: '' }),
        [field]: value,
      },
    }));
  }

  return (
    <div className="mx-auto w-full max-w-2xl space-y-6">
      <ContentPageHeader
        title={st?.warehouse?.name ?? 'Stock take'}
        back={{ label: 'Stock takes', onClick: () => void leaveCountPage() }}
        badge={(
          <div className="flex items-center gap-2">
            {st?.status && <Badge>{st.status}</Badge>}
            {isInitial && <Badge variant="outline">Opening stock</Badge>}
          </div>
        )}
        actions={id && st ? (
          <DeleteStockTakeButton
            stockTakeId={id}
            status={st.status}
            onDeleted={() => navigate('/inventory/stock-takes')}
          />
        ) : null}
      />

      {st?.status === 'REVIEW' && (
        <div className="rounded-lg border bg-card p-4 text-sm flex flex-wrap items-center justify-between gap-3">
          <span>This stock take is in review. Resume counting to enter or change counts.</span>
          <Button size="sm" onClick={resumeCounting} disabled={resuming}>
            {resuming ? 'Resuming…' : 'Resume counting'}
          </Button>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      {assortmentError && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          Could not load location assortment: {assortmentError}
        </div>
      )}

      {canEdit && assortmentOptions.length === 0 && !assortmentError && (
        <div className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
          No products are assigned to this location yet. Add products on the hub or spoke detail page
          before counting.
        </div>
      )}

      {canEdit && (
        <p className="text-sm text-muted-foreground">
          {isInitial
            ? 'Count products that are here. Defer products with no stock — they do not need a batch or expiry. Batch and expiry are required only for products you count.'
            : 'Count products that are here. Defer products with no stock so their quantity stays unchanged. Lines save when you leave a field.'}
        </p>
      )}

      <ProductSelect
        allowEmpty
        emptyLabel="All products on this count"
        placeholder="Filter by product, SKU, or barcode…"
        value={productFilterId}
        onValueChange={(value) => void handleProductSelect(value)}
        products={productOptions}
        triggerClassName="mb-4"
        disabled={productOptions.length === 0}
      />
      <div className="space-y-3">
        {lines.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-8">
            {allLines.length === 0
              ? assortmentOptions.length === 0
                ? 'Assign products to this location, then start or refresh the stock take.'
                : 'Loading product lines…'
              : countableLines.length === 0
                ? 'No active products on this count. Remove stale assortment rows or assign products to this location.'
              : filteredProduct
                ? `No line found for ${filteredProduct.sku} – ${filteredProduct.name}.`
                : 'No products match this filter.'}
          </p>
        ) : (
          lines.map((line) => (
            <CountLineCard
              key={line.id}
              line={line}
              draft={drafts[line.productId] ?? draftFromLine(line)}
              isInitial={isInitial}
              saving={savingLineId === line.id}
              deferring={deferringLineId === line.id}
              saved={draftMatchesLine(drafts[line.productId] ?? draftFromLine(line), line)}
              disabled={!canEdit}
              onDraftChange={(field, value) => updateDraft(line.productId, field, value)}
              onCommit={() => void saveLineIfChanged(line)}
              onDefer={(deferred) => void setLineDeferred(line, deferred)}
            />
          ))
        )}
      </div>
      <p className="text-sm text-muted-foreground mt-4 text-center">
        Counted: {counted} of {countableLines.length} SKUs
        {deferredCount > 0 ? ` · ${deferredCount} deferred` : ''}
        {unsavedCount > 0 ? ` · ${unsavedCount} unsaved` : ''}
        {productFilterId && lines.length < countableLines.length
          ? ` · showing ${lines.length} match${lines.length === 1 ? '' : 'es'}`
          : ''}
      </p>
      {canEdit && (
        <Button className="w-full mt-4" onClick={submitReview} disabled={submitting}>
          {submitting ? 'Submitting…' : 'Submit for review'}
        </Button>
      )}
    </div>
  );
}

function singleUnitPackValid(unitsPerCase: number, leftover: number): boolean {
  const upc = Math.max(1, unitsPerCase);
  if (upc === 1) return leftover === 0;
  return leftover >= 0 && leftover < upc;
}

function CountLineCard({
  line,
  draft,
  isInitial,
  saving,
  deferring,
  saved,
  disabled,
  onDraftChange,
  onCommit,
  onDefer,
}: {
  line: StockTakeLine;
  draft: LineDraft;
  isInitial: boolean;
  saving: boolean;
  deferring: boolean;
  saved: boolean;
  disabled?: boolean;
  onDraftChange: (field: keyof LineDraft, value: string) => void;
  onCommit: () => void;
  onDefer: (deferred: boolean) => void;
}) {
  const upc = Math.max(1, line.product?.unitsPerCase ?? 1);
  const singleUnitPack = upc === 1;
  const deferred = line.isDeferred === true;
  const hasUnsavedEdits = !saved && !deferred;
  const needsBatchDetails = draftNeedsBatchDetails(isInitial, draft, line);
  const countedUnits = draftCountedUnits(draft, line);
  // Batch details only apply once there is stock to book.
  const showBatchFields =
    !deferred &&
    (isInitial ? countedUnits > 0 : countedUnits > line.systemUnits);

  const blurOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  };

  return (
    <Card className={cn(hasUnsavedEdits && !disabled && 'border-amber-200/80')}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <ProductIdentity
            productId={line.productId}
            name={line.product?.name ?? '—'}
            sku={line.product?.sku}
            product={line.product}
            size="sm"
          />
          {!disabled && (
            <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
              {deferred ? (
                <Badge variant="outline">Deferred</Badge>
              ) : saving ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Saving…
                </>
              ) : saved ? (
                <>
                  <Check className="h-3.5 w-3.5 text-green-600" />
                  Saved
                </>
              ) : (
                'Unsaved'
              )}
            </span>
          )}
        </div>
        <div className="text-sm text-muted-foreground">
          {deferred
            ? 'No stock this count. Batch and expiry are not required, and approval will not change the quantity.'
            : isInitial
              ? 'Opening count from zero'
              : `System: ${formatCaseUnits(line.systemUnits, upc)}`}
          {!deferred && (!singleUnitPack ? ` · ${upc} units/case` : ' · sold as single units')}
        </div>
        {!deferred && (
        <div className="flex gap-2">
          <div className="flex-1 space-y-1">
            <Label htmlFor={`cases-${line.id}`} className="text-xs">
              {singleUnitPack ? 'Units' : 'Cases'}
            </Label>
            <Input
              id={`cases-${line.id}`}
              type="number"
              min={0}
              placeholder="0"
              value={draft.cases}
              disabled={disabled}
              onChange={(e) => onDraftChange('cases', e.target.value)}
              onFocus={() => {
                if (draft.cases === '0') onDraftChange('cases', '');
              }}
              onBlur={onCommit}
              onKeyDown={blurOnEnter}
            />
          </div>
          {!singleUnitPack && (
            <div className="flex-1 space-y-1">
              <Label htmlFor={`leftover-${line.id}`} className="text-xs">
                Leftover units
              </Label>
              <Input
                id={`leftover-${line.id}`}
                type="number"
                min={0}
                max={upc - 1}
                placeholder="0"
                value={draft.leftover}
                disabled={disabled}
                onChange={(e) => onDraftChange('leftover', e.target.value)}
                onFocus={() => {
                  if (draft.leftover === '0') onDraftChange('leftover', '');
                }}
                onBlur={onCommit}
                onKeyDown={blurOnEnter}
              />
            </div>
          )}
        </div>
        )}
        {showBatchFields && (
          <div className="flex gap-2">
            <div className="flex-1 space-y-1">
              <Label htmlFor={`batch-${line.id}`} className="text-xs">
                Batch number{isInitial ? ' *' : ' (optional)'}
              </Label>
              <Input
                id={`batch-${line.id}`}
                type="text"
                placeholder="e.g. LOT-2401"
                value={draft.batch}
                disabled={disabled}
                onChange={(e) => onDraftChange('batch', e.target.value)}
                onBlur={onCommit}
                onKeyDown={blurOnEnter}
              />
            </div>
            <div className="flex-1 space-y-1">
              <Label htmlFor={`expiry-${line.id}`} className="text-xs">
                Expiry date{isInitial ? ' *' : ' (optional)'}
              </Label>
              <Input
                id={`expiry-${line.id}`}
                type="date"
                value={draft.expiry}
                disabled={disabled}
                onChange={(e) => onDraftChange('expiry', e.target.value)}
                onBlur={onCommit}
              />
            </div>
          </div>
        )}
        {needsBatchDetails && !disabled && (
          <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
            Batch number and expiry date are required before you can submit for review.
          </p>
        )}
        {!deferred && !singleUnitPack && (
          <p className="text-xs text-muted-foreground">
            Leftover units must be 0–{upc - 1} (partial case).
          </p>
        )}
        {!disabled && (
          <Button
            type="button"
            variant="outline"
            className="w-full"
            disabled={deferring || saving}
            onClick={() => onDefer(!deferred)}
          >
            {deferring ? (deferred ? 'Resuming…' : 'Deferring…') : deferred ? 'Resume counting' : 'Defer'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
